import { useState, useEffect } from 'react';
import TouchpointTimeline from '../TouchpointTimeline/TouchpointTimeline';
import LeadBriefPanel from '../LeadBriefPanel/LeadBriefPanel';
import LogTouchpointModal from '../LogTouchpointModal/LogTouchpointModal';
import {
  getLeadDetail, logTouchpoint, processAi,
  updateLeadStatus, updateInterestLevel, dropLead,
  getOrCreateDraftClient, markProfileComplete, startOnboarding
} from '../../../api/leadApi';
import './LeadDetailPanel.css';

const STATUSES = ['NEW', 'CALLING', 'ENGAGED', 'MEETING_SCHEDULED', 'QUALIFIED'];
const STATUS_LABELS = {
  NEW: 'Just Discovered', CALLING: 'Already Talking', ENGAGED: 'Actively Engaged',
  MEETING_SCHEDULED: 'Meeting Lined Up', QUALIFIED: 'Ready to Invest',
  CONVERTED: 'Converted', DROPPED: 'Dropped'
};
const STATUS_COLORS = {
  NEW: '#8aaab5', CALLING: '#0090c1', ENGAGED: '#f0a500',
  MEETING_SCHEDULED: '#1aaa6e', QUALIFIED: '#004358',
  CONVERTED: '#16a34a', DROPPED: '#dc2626'
};
const INTEREST_LEVELS = ['LOW', 'MEDIUM', 'HIGH'];

export default function LeadDetailPanel({ leadId, onClose, onLeadUpdated, onConverted }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('timeline');
  const [showLogModal, setShowLogModal] = useState(false);
  const [showDropConfirm, setShowDropConfirm] = useState(false);
  const [draftClientId, setDraftClientId] = useState(null);
  const [profilePhase, setProfilePhase] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLead();
  }, [leadId]);

  async function loadLead() {
    setLoading(true);
    setError('');
    try {
      const data = await getLeadDetail(leadId);
      setLead(data);
    } catch (ex) {
      setError(ex.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogTouchpoint(body) {
    const updated = await logTouchpoint(leadId, body);
    setLead(updated);
    if (onLeadUpdated) onLeadUpdated(updated);
  }

  async function handleProcessAi(touchpointId) {
    setProcessingId(touchpointId);
    try {
      const updated = await processAi(leadId, touchpointId, { requestedByUserId: 'current-user' });
      setLead(updated);
      if (onLeadUpdated) onLeadUpdated(updated);
    } catch (ex) {
      alert('AI processing failed: ' + ex.message);
    } finally {
      setProcessingId(null);
    }
  }

  async function handleStatusChange(newStatus) {
    try {
      const updated = await updateLeadStatus(leadId, newStatus, 'current-user');
      setLead(updated);
      if (onLeadUpdated) onLeadUpdated(updated);
    } catch (ex) {
      alert('Status update failed: ' + ex.message);
    }
  }

  async function handleInterestChange(level) {
    try {
      const updated = await updateInterestLevel(leadId, level, 'current-user');
      setLead(updated);
      if (onLeadUpdated) onLeadUpdated(updated);
    } catch (ex) {
      alert('Update failed: ' + ex.message);
    }
  }

  async function handleDrop() {
    try {
      const updated = await dropLead(leadId, null, 'current-user');
      setShowDropConfirm(false);
      if (onLeadUpdated) onLeadUpdated(updated);
      onClose();
    } catch (ex) {
      alert('Drop failed: ' + ex.message);
    }
  }

  async function handleOpenProfileTab() {
    if (draftClientId) return;
    setProfileLoading(true);
    try {
      const client = await getOrCreateDraftClient(leadId);
      setDraftClientId(client._id);
      setProfilePhase(client.client_phase || client.clientPhase || 'DRAFT');
    } catch (ex) {
      console.error('Failed to get/create draft client:', ex);
    } finally {
      setProfileLoading(false);
    }
  }

  async function handleMarkProfileComplete() {
    setProfileLoading(true);
    try {
      await markProfileComplete(leadId, 'current-user');
      setProfilePhase('DRAFT_COMPLETE');
    } catch (ex) {
      alert('Failed: ' + ex.message);
    } finally {
      setProfileLoading(false);
    }
  }

  async function handleStartOnboarding() {
    setProfileLoading(true);
    try {
      const result = await startOnboarding(leadId, null, 'current-user');
      if (onConverted) onConverted(result);
      onClose();
    } catch (ex) {
      alert('Failed to start onboarding: ' + ex.message);
    } finally {
      setProfileLoading(false);
    }
  }

  if (loading) {
    return (
      <>
        <div className="ldp__backdrop" onClick={onClose} />
        <div className="ldp__panel">
          <div className="ldp__loading">Loading...</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="ldp__backdrop" onClick={onClose} />
        <div className="ldp__panel">
          <div className="ldp__error">{error}</div>
        </div>
      </>
    );
  }

  if (!lead) return null;

  const isTerminal = lead.status === 'CONVERTED' || lead.status === 'DROPPED';

  return (
    <>
      <div className="ldp__backdrop" onClick={onClose} />
      <div className="ldp__panel">
        {/* Header */}
        <div className="ldp__header">
          <div className="ldp__header-left">
            <div className="ldp__name">
              {lead.hot_score > 70 && '🔥 '}{lead.name}
            </div>
            <div className="ldp__phone">{lead.phone}</div>
          </div>
          <div className="ldp__header-actions">
            {!isTerminal && (
              <>
                <button className="btn--primary ldp__convert-btn" onClick={() => { setActiveTab('profile'); handleOpenProfileTab(); }}>
                  Start Onboarding →
                </button>
                <button className="btn--outline ldp__drop-btn" onClick={() => setShowDropConfirm(true)}>
                  Drop
                </button>
              </>
            )}
            <button className="ldp__close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Status badge + controls */}
        {!isTerminal && (
          <div className="ldp__controls">
            <div className="ldp__control-row">
              <span className="ldp__control-label">Status</span>
              <div className="ldp__status-pills">
                {STATUSES.map(s => (
                  <button
                    key={s}
                    className={`ldp__status-pill ${lead.status === s ? 'ldp__status-pill--active' : ''}`}
                    style={lead.status === s ? { background: STATUS_COLORS[s], borderColor: STATUS_COLORS[s] } : {}}
                    onClick={() => handleStatusChange(s)}
                  >
                    {STATUS_LABELS[s] || s}
                  </button>
                ))}
              </div>
            </div>
            <div className="ldp__control-row">
              <span className="ldp__control-label">Interest</span>
              <div className="ldp__interest-pills">
                {INTEREST_LEVELS.map(lvl => (
                  <button
                    key={lvl}
                    className={`ldp__interest-pill ldp__interest-pill--${lvl.toLowerCase()} ${lead.interest_level === lvl ? 'ldp__interest-pill--active' : ''}`}
                    onClick={() => handleInterestChange(lvl)}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {isTerminal && (
          <div className="ldp__terminal-badge" style={{ background: STATUS_COLORS[lead.status] }}>
            {STATUS_LABELS[lead.status] || lead.status}
            {lead.converted_client_id && ` · Client ID: ${lead.converted_client_id}`}
            {lead.drop_reason && ` · ${lead.drop_reason}`}
          </div>
        )}

        {/* Tabs */}
        <div className="ldp__tabs">
          <button
            className={`ldp__tab ${activeTab === 'timeline' ? 'ldp__tab--active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            Timeline
          </button>
          <button
            className={`ldp__tab ${activeTab === 'brief' ? 'ldp__tab--active' : ''}`}
            onClick={() => setActiveTab('brief')}
          >
            AI Brief
          </button>
          <button
            className={`ldp__tab ${activeTab === 'profile' ? 'ldp__tab--active' : ''}`}
            onClick={() => { setActiveTab('profile'); handleOpenProfileTab(); }}
          >
            Profile
          </button>
        </div>

        {/* Tab content */}
        <div className="ldp__tab-content">
          {activeTab === 'timeline' && (
            <TouchpointTimeline
              touchpoints={lead.touchpoints || []}
              onLogTouchpoint={() => setShowLogModal(true)}
              onProcessAi={handleProcessAi}
              processingId={processingId}
            />
          )}
          {activeTab === 'brief' && (
            <LeadBriefPanel leadId={leadId} />
          )}
          {activeTab === 'profile' && (
            <div className="ldp__profile-tab">
              {profileLoading && <div className="ldp__profile-loading">Loading...</div>}
              {!profileLoading && draftClientId && (
                <>
                  <div className="ldp__profile-client-id">
                    Profile ID: <span className="ldp__profile-mono">{draftClientId}</span>
                  </div>
                  <div className="ldp__profile-phase-badge">
                    {profilePhase === 'DRAFT' && 'Profile in progress'}
                    {profilePhase === 'DRAFT_COMPLETE' && '✓ Profile complete — awaiting onboarding'}
                    {profilePhase === 'ONBOARDING' && 'Onboarding in progress'}
                  </div>
                  {profilePhase === 'DRAFT' && (
                    <button className="btn btn--primary ldp__profile-action-btn" onClick={handleMarkProfileComplete}>
                      Mark Profile Complete
                    </button>
                  )}
                  {profilePhase === 'DRAFT_COMPLETE' && !isTerminal && (
                    <button className="btn btn--success ldp__profile-action-btn" onClick={handleStartOnboarding}>
                      Start Onboarding →
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Drop confirm */}
      {showDropConfirm && (
        <div className="ldp__confirm-overlay" onClick={() => setShowDropConfirm(false)}>
          <div className="ldp__confirm-box" onClick={e => e.stopPropagation()}>
            <div className="ldp__confirm-title">Drop this lead?</div>
            <div className="ldp__confirm-text">
              This will mark <strong>{lead.name}</strong> as dropped. The record will still be preserved.
            </div>
            <div className="ldp__confirm-actions">
              <button className="btn--outline" onClick={() => setShowDropConfirm(false)}>Cancel</button>
              <button className="btn--outline ldp__confirm-drop" onClick={handleDrop}>Yes, Drop</button>
            </div>
          </div>
        </div>
      )}

      {showLogModal && (
        <LogTouchpointModal
          lead={lead}
          onClose={() => setShowLogModal(false)}
          onSaved={handleLogTouchpoint}
        />
      )}

    </>
  );
}
